import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { webApi } from '../services/api';
import { isCanceledRequest } from '../utils/abortRequest';

const OPTIONS = [
  {
    value: 'individual',
    title: 'Individual',
    description: 'Tips go directly to the scanned employee'
  },
  {
    value: 'pool',
    title: 'Pool',
    description: 'Tips are shared among all employees in the pool'
  }
];

const PROVIDER_OPTIONS = [
  { value: 'razorpay', title: 'Razorpay (direct QR payment)' },
  { value: 'cashfree', title: 'Cashfree (review-capable pay flow)' }
];

function normalizeMode(m) {
  const v = (m || '').toLowerCase();
  return v === 'pool' || v === 'individual' ? v : 'individual';
}

function displayName(mode) {
  const n = normalizeMode(mode);
  return n === 'pool' ? 'Pool' : 'Individual';
}

function PersonIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
      />
    </svg>
  );
}

/** Pool — from Android `drawable/ic_group.xml` (512 viewport, scaled to 24px). */
function GroupPeopleIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 512 512" aria-hidden="true">
      <path
        fill="currentColor"
        d="m157.2,256c0,24.2 8.8,47.6 24.9,65.6h-155.6c-14.6,0 -26.5,-11.8 -26.5,-26.5v-14.1c-0.1,-33 13.8,-64.4 38.3,-86.6 8.8,-8 21.9,-9.1 31.9,-2.7 13.9,8.8 30.1,13.4 46.6,13.3 16.5,0.1 32.7,-4.5 46.6,-13.3 6.1,-3.9 13.6,-5.1 20.6,-3.4 -17.3,18.4 -26.8,42.6 -26.8,67.7z"
      />
      <path
        fill="currentColor"
        d="M116.7,105.9m-65.6,0a65.6,65.6 0,1 1,131.2 0a65.6,65.6 0,1 1,-131.2 0"
      />
      <path
        fill="currentColor"
        d="m512,281v14.1c0,14.6 -11.9,26.5 -26.5,26.5h-155.7c33.9,-38.2 33.2,-96 -1.8,-133.2 7,-1.8 14.5,-0.6 20.6,3.4 28.5,17.7 64.7,17.7 93.2,0 10,-6.4 23.1,-5.3 31.9,2.7 24.5,22.1 38.4,53.5 38.3,86.5z"
      />
      <path
        fill="currentColor"
        d="M395.3,105.9m-65.6,0a65.6,65.6 0,1 1,131.2 0a65.6,65.6 0,1 1,-131.2 0"
      />
      <path
        fill="currentColor"
        d="m256,355.2c16.5,0.1 32.7,-4.5 46.6,-13.3 10,-6.4 23.1,-5.3 31.9,2.7 24.4,22.1 38.3,53.6 38.3,86.6v14.1c0,14.6 -11.9,26.5 -26.5,26.5h-180.5c-14.6,0 -26.5,-11.8 -26.5,-26.5v-14.1c-0.1,-33 13.8,-64.4 38.3,-86.6 8.8,-8 21.9,-9.1 31.9,-2.7 13.9,8.8 30,13.4 46.5,13.3z"
      />
      <path
        fill="currentColor"
        d="M256,256m-65.6,0a65.6,65.6 0,1 1,131.2 0a65.6,65.6 0,1 1,-131.2 0"
      />
    </svg>
  );
}

function OptionCheckIcon() {
  return (
    <svg className="tip-mode-option-check" width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10.5" fill="#fff" />
      <path fill="var(--primary)" d="M10 15.17l-3.5-3.5-1.41 1.42L10 18l7.07-7.07-1.41-1.42L10 15.17z" />
    </svg>
  );
}

function TipModePage() {
  const navigate = useNavigate();
  const [currentTipMode, setCurrentTipMode] = useState('individual');
  const [selectedTipMode, setSelectedTipMode] = useState('individual');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState({ kind: '', text: '' });
  const [poolPaymentProvider, setPoolPaymentProvider] = useState('razorpay');
  const [reviewInclusionEnabled, setReviewInclusionEnabled] = useState(false);
  const [tipModeManagedByFranchise, setTipModeManagedByFranchise] = useState(false);
  const [franchiseLabel, setFranchiseLabel] = useState('Franchise');

  const load = useCallback(async (signal) => {
    setLoading(true);
    setBanner({ kind: '', text: '' });
    try {
      const data = await webApi.getShopInfo({ signal });
      if (signal.aborted) return;
      const mode = normalizeMode(data?.defaultTipMode);
      setCurrentTipMode(mode);
      setSelectedTipMode(mode);
      setPoolPaymentProvider((data?.poolPaymentProvider || 'razorpay').toLowerCase());
      setReviewInclusionEnabled(Boolean(data?.reviewInclusionEnabled));
      setTipModeManagedByFranchise(Boolean(data?.tipModeManagedByFranchise));
      setFranchiseLabel(
        String(data?.franchiseLabel || '').trim() || 'Franchise'
      );
    } catch (err) {
      if (signal.aborted || isCanceledRequest(err)) return;
      setBanner({ kind: 'error', text: err.message || 'Failed to load tip type' });
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const dirty = normalizeMode(selectedTipMode) !== normalizeMode(currentTipMode);
  const canSave = dirty && !saving && !loading && !tipModeManagedByFranchise;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setBanner({ kind: '', text: '' });
    try {
      const mode = normalizeMode(selectedTipMode);
      await webApi.updateTipMode({ tipMode: mode });
      await webApi.updatePoolPaymentProvider({
        poolPaymentProvider,
        reviewInclusionEnabled
      });
      setCurrentTipMode(mode);
      setSelectedTipMode(mode);
      setBanner({ kind: 'success', text: 'Tip type updated successfully' });
    } catch (err) {
      setBanner({ kind: 'error', text: err.message || 'Failed to update tip type' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tip-mode-page">
      <header className="tip-mode-topbar">
        <button type="button" className="tip-mode-back" onClick={() => navigate(-1)} aria-label="Go back">
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </button>
        <h1 className="tip-mode-topbar-title">Change Tip Type</h1>
        <span className="tip-mode-topbar-spacer" aria-hidden="true" />
      </header>

      <div className="tip-mode-body">
        {banner.text ? (
          <p className={`tip-mode-banner tip-mode-banner--${banner.kind}`} role="status">
            {banner.text}
          </p>
        ) : null}

        {loading ? (
          <p className="tip-mode-loading">Loading…</p>
        ) : (
          <>
            <section className="tip-mode-current-card" aria-label="Current tip type">
              <p className="tip-mode-current-label">Current Tip Type</p>
              <p className="tip-mode-current-value">{displayName(currentTipMode)}</p>
              {tipModeManagedByFranchise ? (
                <p className="tip-mode-option-desc" style={{ marginTop: '0.35rem' }}>
                  Managed by {franchiseLabel}
                </p>
              ) : null}
            </section>

            <h2 className="tip-mode-section-label">Select Tip Type</h2>

            {OPTIONS.map((opt) => {
              const selected = normalizeMode(selectedTipMode) === opt.value;
              const lockedOut = tipModeManagedByFranchise && !selected;
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`tip-mode-option${selected ? ' tip-mode-option--selected' : ''}`}
                  onClick={() => {
                    if (tipModeManagedByFranchise) return;
                    setSelectedTipMode(opt.value);
                  }}
                  disabled={tipModeManagedByFranchise}
                  style={lockedOut ? { opacity: 0.45 } : undefined}
                >
                  <span
                    className={`tip-mode-option-icon${selected ? ' tip-mode-option-icon--on-dark' : ''}`}
                    aria-hidden="true"
                  >
                    {opt.value === 'individual' ? <PersonIcon /> : <GroupPeopleIcon />}
                  </span>
                  <span className="tip-mode-option-text">
                    <span className="tip-mode-option-title">{opt.title}</span>
                    <span className="tip-mode-option-desc">{opt.description}</span>
                  </span>
                  {selected ? <OptionCheckIcon /> : null}
                </button>
              );
            })}

            <button
              type="button"
              className="tip-mode-save"
              disabled={!canSave}
              onClick={save}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>

            <h2 className="tip-mode-section-label" style={{ marginTop: '1.5rem' }}>Pool Payment Provider</h2>
            <div role="group" aria-label="Pool payment provider">
              {PROVIDER_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`tip-mode-option${poolPaymentProvider === opt.value ? ' tip-mode-option--selected' : ''}`}
                  onClick={() => setPoolPaymentProvider(opt.value)}
                >
                  <span className="tip-mode-option-text">
                    <span className="tip-mode-option-title">{opt.title}</span>
                  </span>
                  {poolPaymentProvider === opt.value ? <OptionCheckIcon /> : null}
                </button>
              ))}
            </div>
            <label className="tip-mode-loading" style={{ display: 'block', marginTop: '0.75rem' }}>
              <input
                type="checkbox"
                checked={reviewInclusionEnabled}
                onChange={(e) => setReviewInclusionEnabled(e.target.checked)}
                style={{ marginRight: '0.5rem' }}
              />
              Enable review inclusion in pool flow (uses configured fallback flow when needed)
            </label>
          </>
        )}
      </div>
    </div>
  );
}

export default TipModePage;
